import { useCallback, useEffect, useState } from "react";

import { api, type GoogleSheetsIntegrationStatus } from "../lib/api";

const AUTHORIZATION_CHANNEL = "ba-google-sheets-integration";
type IntegrationAction =
  "connect" | "reconnect" | "disconnect" | "manage" | "revoke";

/** Build the platform-controlled popup URL for one integration action. */
export function integrationActionUrl(
  action: IntegrationAction,
  artifactSlug = "",
): string {
  if (action === "connect") return "/api/google-sheets/connect";
  if (action === "reconnect") return "/api/google-sheets/connect?force=true";
  if (action === "disconnect") return "/api/google-sheets/manage/disconnect";
  if (action === "manage") return "/api/google-sheets/manage/grants";
  return `/api/google-sheets/manage/grants/${encodeURIComponent(artifactSlug)}/revoke`;
}

/** Return the viewer-facing label for the currently granted OAuth mode. */
export function scopeLabel(
  scope: GoogleSheetsIntegrationStatus["scope_mode"],
): string {
  if (scope === "read_write") return "Read and write access";
  // A read-only grant serves neither tool any more (MCP-23: Google's query
  // endpoint rejects it), so the label tells the viewer what to do.
  if (scope === "read_only") return "Read-only — reconnect to use Google Sheets";
  return "Not connected";
}

/** Identify BroadcastChannel messages that require a fresh status request. */
export function isAuthorizationRefreshMessage(value: unknown): boolean {
  if (typeof value !== "object" || value === null || !("status" in value))
    return false;
  const status = (value as { status?: unknown }).status;
  return ["approved", "connected", "disconnected", "grant_revoked"].includes(
    String(status),
  );
}

/** Select the visible integration state after an initial or retrying request. */
export function integrationViewState(
  status: GoogleSheetsIntegrationStatus | null,
  loading: boolean,
): "loading" | "unavailable" | "ready" {
  if (status) return "ready";
  return loading ? "loading" : "unavailable";
}

/** Open a user-activated platform popup and report whether the browser allowed it. */
function openIntegrationPopup(action: IntegrationAction, slug = ""): boolean {
  return (
    window.open(
      integrationActionUrl(action, slug),
      "ba-google-sheets-integration",
      "popup=yes,width=620,height=760",
    ) !== null
  );
}

/** Viewer page for connecting Google Sheets and managing artifact approvals. */
export function IntegrationsPage(): JSX.Element {
  const [status, setStatus] = useState<GoogleSheetsIntegrationStatus | null>(
    null,
  );
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const viewState = integrationViewState(status, loading);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      setStatus(await api.googleSheets.integration.get());
      setError("");
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Unable to load integration",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!("BroadcastChannel" in window)) return undefined;
    const channel = new BroadcastChannel(AUTHORIZATION_CHANNEL);
    channel.onmessage = (event: MessageEvent<unknown>) => {
      if (isAuthorizationRefreshMessage(event.data)) void refresh();
    };
    return () => channel.close();
  }, [refresh]);

  /** Open one integration action directly from the viewer's button click. */
  const launch = (action: IntegrationAction, slug = ""): void => {
    if (!openIntegrationPopup(action, slug)) {
      setError(
        "The browser blocked the Google Sheets popup. Allow popups and try again.",
      );
    }
  };

  return (
    <section className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Integrations</h1>
        <p className="mt-1 text-sm text-slate-600">
          Connect your Google account once, then approve each artifact&apos;s
          exact spreadsheet ranges and access modes. Google grants read and
          edit access to your Sheets; artifacts you view in a browser are
          limited to the ranges and modes you approve. Your own MCP clients
          read through this connection directly and are not limited to those
          ranges (they cannot write). Disconnecting Google is the only thing
          that revokes both.
        </p>
      </div>
      {error && (
        <p
          role="alert"
          className="rounded-md bg-red-50 p-3 text-sm text-red-700"
        >
          {error}
        </p>
      )}
      {viewState === "loading" ? (
        <p className="text-sm text-slate-500">
          Loading Google Sheets integration…
        </p>
      ) : status === null ? (
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <p className="text-sm text-slate-600">
            Google Sheets integration status is unavailable.
          </p>
          <button
            type="button"
            onClick={() => void refresh()}
            className="mt-3 rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700"
          >
            Try again
          </button>
        </div>
      ) : !status.available ? (
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <h2 className="font-semibold text-slate-900">Google Sheets</h2>
          <p className="mt-1 text-sm text-slate-600">
            This integration is not available yet. An administrator must finish
            the dedicated Google OAuth and encryption configuration.
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-lg border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="font-semibold text-slate-900">Google Sheets</h2>
                <p className="mt-1 text-sm text-slate-600">
                  {scopeLabel(status.scope_mode)}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() =>
                    launch(status.connected ? "reconnect" : "connect")
                  }
                  className="rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700"
                >
                  {status.connected ? "Reconnect" : "Connect"}
                </button>
                {status.connected && (
                  <button
                    type="button"
                    onClick={() => launch("disconnect")}
                    className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                  >
                    Disconnect
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-5">
            <h2 className="font-semibold text-slate-900">Artifact approvals</h2>
            <p className="mt-1 text-sm text-slate-600">
              Open the isolated approval manager to review exact artifact ranges
              or revoke individual grants.
            </p>
            <button
              type="button"
              onClick={() => launch("manage")}
              className="mt-3 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
            >
              Manage approvals
            </button>
          </div>
        </>
      )}
    </section>
  );
}
